import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axiosConfig';
import './Master.css'
import { FaArrowUp, FaArrowDown, FaReply, FaTimes, FaPlay, FaImage, FaVideo, FaLink } from 'react-icons/fa';
import EditDeleteControls from './EditDeleteControls';
import CreatePost from './CreatePost';
import GiphyLogo from "../../Assets/Images/PoweredBy_200px-White_HorizText.png";

const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

const BASE_S3_URL = "https://messageboard-images.s3.amazonaws.com";

// Video Player Component
const VideoPlayer = ({ video, autoplay = false }) => {
    const [isPlaying, setIsPlaying] = useState(false);

    console.log('VideoPlayer component - video prop:', video);

    if (!video) {
        console.log('VideoPlayer: No video provided');
        return null;
    }

    const handlePlay = () => {
        console.log('Video play clicked');
        setIsPlaying(true);
    };

    return (
        <div className="video-container">
            {!isPlaying ? (
                <div className="video-thumbnail" onClick={handlePlay}>
                    <img
                        src={video.thumbnail_url}
                        alt="Video thumbnail"
                        onLoad={() => console.log('Thumbnail loaded successfully')}
                        onError={(e) => {
                            console.error('Thumbnail failed to load:', video.thumbnail_url);
                            console.error('Error event:', e);
                        }}
                    />
                    <div className="video-play-overlay">
                        <FaPlay className="play-icon" />
                        <div className="video-duration">
                            {Math.round(video.duration)}s
                        </div>
                    </div>
                </div>
            ) : (
                <video
                    controls
                    autoPlay={autoplay}
                    src={video.video_url}
                    poster={video.thumbnail_url}
                    style={{ width: '100%', maxHeight: '400px', borderRadius: '8px' }}
                    onLoadStart={() => console.log('Video loading started')}
                    onError={(e) => {
                        console.error('Video failed to load:', video.video_url);
                        console.error('Video error event:', e);
                    }}
                >
                    Your browser does not support the video tag.
                </video>
            )}
        </div>
    );
};

// Image Gallery Component
const ImageGallery = ({ images }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    if (!images || images.length === 0) return null;

    const openLightbox = (index) => {
        setCurrentImageIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
        <div className="image-gallery">
            {images.length === 1 ? (
                <img
                    src={images[0].image_url}
                    alt="Message attachment"
                    className="single-image"
                    onClick={() => openLightbox(0)}
                />
            ) : (
                <div className={`image-grid ${images.length === 2 ? 'two-images' : images.length === 3 ? 'three-images' : 'four-plus-images'}`}>
                    {images.slice(0, 4).map((image, index) => (
                        <div key={image.id} className="image-container" onClick={() => openLightbox(index)}>
                            <img src={image.image_url} alt={`Message attachment ${index + 1}`} />
                            {index === 3 && images.length > 4 && (
                                <div className="more-images-overlay">
                                    +{images.length - 4}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {lightboxOpen && (
                <div className="lightbox-overlay" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={closeLightbox}>
                            <FaTimes />
                        </button>

                        <img src={images[currentImageIndex].image_url} alt="Enlarged view" />

                        {images.length > 1 && (
                            <>
                                <button className="lightbox-nav prev" onClick={prevImage}>
                                    ←
                                </button>
                                <button className="lightbox-nav next" onClick={nextImage}>
                                    →
                                </button>
                                <div className="lightbox-counter">
                                    {currentImageIndex + 1} / {images.length}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const Comment = ({ comment, messageId, onReply, onVote, depth = 0, isTopLevel, currentUser, setMessages }) => {
    const [isReplying, setIsReplying] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [replyImages, setReplyImages] = useState([]);
    const [replyGifUrl, setReplyGifUrl] = useState('');
    const [replyLinkUrl, setReplyLinkUrl] = useState('');
    const [showReplyLinkInput, setShowReplyLinkInput] = useState(false);
    const [showReplyGifSearch, setShowReplyGifSearch] = useState(false);
    const [giphyResults, setGiphyResults] = useState([]);
    const [giphyQuery, setGiphyQuery] = useState('');
    const [username, setUsername] = useState(comment.user);
    const [showNestedReplies, setShowNestedReplies] = useState(depth < 2);

    // NEW: modal state + data for deep threads
    const [showModal, setShowModal] = useState(false);
    const [modalReplies, setModalReplies] = useState([]);

    const isOwner = currentUser && (comment.user === currentUser.username || comment.user === currentUser.id);

    useEffect(() => {
        if (isTopLevel) {
            console.log('=== DEBUG MESSAGE ===');
            console.log('Comment ID:', comment.id);
            console.log('Full comment data:', comment);
            console.log('Has video:', !!comment.video);
            console.log('Has images:', comment.images?.length > 0);
            console.log('===================');
        }
    }, [comment, isTopLevel]);

    const handleEdit = (editedMessageId, newTitle, newContent) => {
        setMessages((prevMessages) =>
            prevMessages.map((msg) => {
                if (msg.id === editedMessageId) {
                    return { ...msg, title: newTitle, content: newContent };
                }
                const updateReplies = (replies) =>
                    replies.map((reply) =>
                        reply.id === editedMessageId
                            ? { ...reply, content: newContent }
                            : { ...reply, replies: updateReplies(reply.replies || []) }
                    );
                return { ...msg, replies: updateReplies(msg.replies || []) };
            })
        );
    };

    const handleDelete = (deletedMessageId) => {
        setMessages((prevMessages) =>
            prevMessages
                .filter((msg) => msg.id !== deletedMessageId)
                .map((msg) => {
                    const updateReplies = (replies) =>
                        replies.filter((reply) => reply.id !== deletedMessageId);
                    return { ...msg, replies: updateReplies(msg.replies || []) };
                })
        );
    };

    useEffect(() => {
        if (typeof comment.user === 'number') {
            fetchUsername(comment.user, setUsername);
        }
    }, [comment.user]);

    const fetchUsername = async (userId, setUsernameCallback) => {
        try {
            const response = await axiosInstance.get(`/messageboard/usernames/${userId}/`);
            setUsernameCallback(response.data.username);
        } catch (error) {
            console.error("Error fetching username:", error);
        }
    };

    // GIF search functionality
    const handleSearchGiphy = async (query) => {
        if (!query.trim()) { setGiphyResults([]); return; }
        try {
            const response = await fetch(
                `https://api.giphy.com/v1/gifs/search?api_key=EllgnXD7w2MrtrFkBep20b9jxMLpkraw&q=${encodeURIComponent(query)}&limit=8`
            );
            const data = await response.json();
            setGiphyResults(data.data || []);
        } catch (error) {
            console.error('Error searching GIFs:', error);
            setGiphyResults([]);
        }
    };

    const handleSelectGif = (url) => {
        setReplyGifUrl(url);
        setGiphyResults([]);
        setGiphyQuery('');
        setShowReplyGifSearch(false);
    };

    const handleImageUpload = (e) => setReplyImages((prev) => [...prev, ...Array.from(e.target.files)]);
    const removeReplyImage = (index) => setReplyImages((prev) => prev.filter((_, i) => i !== index));

    const validateUrl = (url) => { try { new URL(url); return true; } catch { return false; } };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyContent.trim() && !replyGifUrl && replyImages.length === 0 && !replyLinkUrl) {
            alert('Please add some content to your reply');
            return;
        }
        if (replyLinkUrl && !validateUrl(replyLinkUrl)) {
            alert('Please enter a valid URL (including http:// or https://)');
            return;
        }

        try {
            await onReply(messageId, depth === 0 ? null : comment.id, {
                content: replyContent,
                images: replyImages,
                gifUrl: replyGifUrl,
                linkUrl: replyLinkUrl
            });

            // reset
            setReplyContent('');
            setReplyImages([]);
            setReplyGifUrl('');
            setReplyLinkUrl('');
            setIsReplying(false);
            setShowReplyLinkInput(false);
            setShowReplyGifSearch(false);
            setGiphyQuery('');
            setGiphyResults([]);
        } catch (error) {
            console.error('Error submitting reply:', error);
            alert('Error submitting reply. Please try again.');
        }
    };

    // OPEN MODAL (for depth >= 2)
    const openModal = () => {
        setShowModal(true);
        const fetchModalReplies = (comment.replies || []).map(async (reply) => {
            if (typeof reply.user === 'number' && !reply.username) {
                try {
                    const r = await axiosInstance.get(`/messageboard/usernames/${reply.user}/`);
                    return { ...reply, username: r.data.username };
                } catch {
                    return { ...reply, username: 'Unknown User' };
                }
            }
            return reply;
        });
        Promise.all(fetchModalReplies).then((fetched) => setModalReplies(fetched));
    };

    const closeModal = () => setShowModal(false);

    return (
        <li style={{ marginLeft: depth === 0 ? 0 : depth * 20 }} className={`reply depth-${depth} ${isTopLevel ? 'post-container' : ''}`}>
            <div>
                <div className="post-meta">
                    <strong className="username-link">{username}</strong>
                    <span className="reply-date">• {formatDate(comment.created_at)}</span>
                </div>
                {isTopLevel && comment.title && <h4 className="message-title">{comment.title}</h4>}
                <p className="reply-text">{comment.content}</p>

                {isOwner && (
                    <EditDeleteControls
                        messageId={comment.id}
                        isReply={!isTopLevel}
                        originalTitle={isTopLevel ? comment.title : null}
                        originalContent={comment.content}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                )}
            </div>

            {/* Top-level media */}
            {isTopLevel && (
                <div className="post-media-container">
                    {comment.video ? (
                        <VideoPlayer video={comment.video} />
                    ) : comment.images && comment.images.length > 0 ? (
                        <ImageGallery images={comment.images} />
                    ) : null}
                </div>
            )}

            {/* Top-level links - this was missing! */}
            {isTopLevel && comment.link_url && (
                <div className="post-link">
                    <FaLink className="link-icon" />
                    <a href={comment.link_url} target="_blank" rel="noopener noreferrer">{comment.link_url}</a>
                </div>
            )}

            {/* Non-top-level media inline (unchanged) */}
            {!isTopLevel && (
                <div className="reply-media-container">
                    {comment.images?.length > 0 && (
                        <div className="reply-images"><ImageGallery images={comment.images} /></div>
                    )}
                    {comment.gif_url && (
                        <div className="reply-gif-container"><img src={comment.gif_url} alt="Reply GIF" className="reply-gif" /></div>
                    )}
                    {comment.link_url && (
                        <div className="post-link">
                            <FaLink className="link-icon" />
                            <a href={comment.link_url} target="_blank" rel="noopener noreferrer">{comment.link_url}</a>
                        </div>
                    )}
                </div>
            )}

            <div className="reply-actions">
                <div className="vote-buttons">
                    <FaArrowUp onClick={() => onVote(comment.id, 'upvote', depth)} />
                    <span>{comment.upvotes}</span>
                    <FaArrowDown onClick={() => onVote(comment.id, 'downvote', depth)} />
                    <span>{comment.downvotes}</span>
                </div>

                <button onClick={() => setIsReplying(!isReplying)} className="reply-button">
                    <FaReply /> Reply
                </button>
            </div>

            {isReplying && (
                <div className="nested-reply-form">
                    <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder={`Replying to ${username}...`}
                        className="nested-reply-textarea"
                    />

                    <div className="nested-reply-controls">
                        <label className="media-button-small">
                            <FaImage />
                            <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="visually-hidden" />
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowReplyGifSearch(!showReplyGifSearch)}
                            className={`media-button-small ${showReplyGifSearch ? 'active' : ''}`}
                        >
                            🎬
                        </button>
                        {!showReplyLinkInput && (
                            <button type="button" onClick={() => setShowReplyLinkInput(true)} className="media-button-small">
                                <FaLink />
                            </button>
                        )}
                    </div>

                    {showReplyLinkInput && (
                        <div className="nested-link-input">
                            <input
                                type="url"
                                placeholder="https://example.com"
                                value={replyLinkUrl}
                                onChange={(e) => setReplyLinkUrl(e.target.value)}
                            />
                            <button onClick={() => { setShowReplyLinkInput(false); setReplyLinkUrl(''); }}>
                                <FaTimes />
                            </button>
                        </div>
                    )}

                    {showReplyGifSearch && (
                        <div className="nested-gif-search">
                            <input
                                type="text"
                                placeholder="Search GIFs..."
                                value={giphyQuery}
                                onChange={(e) => { setGiphyQuery(e.target.value); handleSearchGiphy(e.target.value); }}
                            />
                            <div className="nested-giphy-results">
                                {giphyResults.map((gif) => (
                                    <img
                                        key={gif.id}
                                        src={gif.images.fixed_height_small.url}
                                        alt="GIF option"
                                        onClick={() => handleSelectGif(gif.images.fixed_height.url)}
                                        className="nested-giphy-result"
                                    />
                                ))}
                            </div>
                            {giphyResults.length > 0 && (
                                <div className="giphy-attribution">
                                    <img src={GiphyLogo} alt="Powered by GIPHY" className="giphy-logo" />
                                </div>
                            )}
                        </div>
                    )}

                    {replyImages.length > 0 && (
                        <div className="nested-image-previews">
                            {replyImages.map((image, index) => (
                                <div key={index} className="nested-image-preview">
                                    <img src={URL.createObjectURL(image)} alt={`Preview ${index + 1}`} />
                                    <button onClick={() => removeReplyImage(index)}><FaTimes /></button>
                                </div>
                            ))}
                        </div>
                    )}

                    {replyGifUrl && (
                        <div className="nested-gif-preview">
                            <img src={replyGifUrl} alt="Selected GIF" />
                            <button onClick={() => setReplyGifUrl('')}><FaTimes /></button>
                        </div>
                    )}

                    <div className="nested-reply-actions">
                        <button type="button" onClick={() => setIsReplying(false)} className="cancel-reply-btn">
                            Cancel
                        </button>
                        <button onClick={handleReply} className="submit-reply-btn">
                            Post Reply
                        </button>
                    </div>
                </div>
            )}

            {comment.replies && comment.replies.length > 0 && (
                <>
                    {depth >= 2 ? (
                        <div className="nested-reply-toggle" onClick={openModal}>
                            Show {comment.replies.length} replies
                        </div>
                    ) : (
                        <ul className="replies-container">
                            {comment.replies
                                .filter(reply => reply.parent === comment.id || (reply.parent === null && depth === 0))
                                .map(reply => (
                                    <Comment
                                        key={reply.id}
                                        comment={reply}
                                        messageId={messageId}
                                        onReply={onReply}
                                        onVote={onVote}
                                        depth={depth + 1}
                                        isTopLevel={false}
                                        setMessages={setMessages}
                                        currentUser={currentUser}
                                    />
                                ))}
                        </ul>
                    )}
                </>
            )}

            {/* UPDATED MODAL: now shows images, GIFs, links (and parent video if present) */}
            {showModal && (
                <div className="reply-modal-overlay" onClick={closeModal}>
                    <div className="reply-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="reply-modal-close" onClick={closeModal}>✖</div>

                        <div className="reply-thread-container">
                            {/* Parent comment (context) */}
                            <div className="reply-thread-comment">
                                <strong>{username} • {formatDate(comment.created_at)}</strong>
                                {comment.title && <div className="message-title" style={{ marginTop: 4 }}>{comment.title}</div>}
                                {comment.content && <p>{comment.content}</p>}

                                {/* Parent media */}
                                {comment.video && (
                                    <div className="reply-modal-media"><VideoPlayer video={comment.video} /></div>
                                )}
                                {comment.images?.length > 0 && (
                                    <div className="reply-modal-media"><ImageGallery images={comment.images} /></div>
                                )}
                                {comment.gif_url && (
                                    <div className="reply-gif-container"><img src={comment.gif_url} alt="GIF" className="reply-gif" /></div>
                                )}
                                {comment.link_url && (
                                    <div className="post-link">
                                        <FaLink className="link-icon" />
                                        <a href={comment.link_url} target="_blank" rel="noopener noreferrer">{comment.link_url}</a>
                                    </div>
                                )}
                            </div>

                            {/* Child replies displayed inside modal with their media */}
                            {modalReplies.map(reply => (
                                <div key={reply.id} className="reply-thread-comment">
                                    <strong>{reply.username || reply.user} • {formatDate(reply.created_at)}</strong>
                                    {reply.content && <p>{reply.content}</p>}

                                    {reply.images?.length > 0 && (
                                        <div className="reply-modal-media"><ImageGallery images={reply.images} /></div>
                                    )}
                                    {reply.gif_url && (
                                        <div className="reply-gif-container"><img src={reply.gif_url} alt="GIF" className="reply-gif" /></div>
                                    )}
                                    {reply.link_url && (
                                        <div className="post-link">
                                            <FaLink className="link-icon" />
                                            <a href={reply.link_url} target="_blank" rel="noopener noreferrer">{reply.link_url}</a>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </li>
    );
};

const MessageBoard = ({ course_id, club_id, postId }) => {
    const [messages, setMessages] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await axiosInstance.get('/auth/current-user/');
                setCurrentUser(response.data);
            } catch (error) {
                console.error('Error fetching current user:', error);
            }
        };
        fetchCurrentUser();
        fetchMessages();
    }, [course_id, club_id, postId]);

    const fetchMessages = async () => {
        try {
            if (postId) {
                const response = await axiosInstance.get(`/messageboard/messages/${postId}/`);
                setMessages([response.data]);
            } else {
                // Prefer club_id (matches mobile behavior — shows all posts for the club)
                // Fall back to course_id for backward compatibility
                const params = club_id ? { club_id } : { course_id };
                const response = await axiosInstance.get('/messageboard/messages/', { params });
                setMessages(response.data);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        }
    };

    // Handle new message creation from CreatePost component
    const handlePostCreated = (newPost) => {
        console.log('New message created:', newPost);
        setMessages((prevMessages) => [newPost, ...prevMessages]);
    };

    const handleReplySubmit = async (messageId, parentId, replyData) => {
        try {
            const formData = new FormData();
            formData.append('content', replyData.content || '');

            if (parentId) {
                formData.append('parent', parentId);
            }

            if (replyData.gifUrl) {
                formData.append('gif_url', replyData.gifUrl);
            }

            if (replyData.linkUrl) {
                formData.append('link_url', replyData.linkUrl);
            }

            if (replyData.images && replyData.images.length > 0) {
                replyData.images.forEach((image) => {
                    formData.append('images', image, image.name);
                });
            }

            await axiosInstance.post(`/messageboard/messages/${messageId}/replies/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            fetchMessages();
        } catch (error) {
            console.error('Error posting reply:', error);
            if (error.response?.data?.error) {
                alert(`Error: ${error.response.data.error}`);
            } else {
                alert('An error occurred while submitting your reply');
            }
            throw error;
        }
    };

    const handleVote = async (id, type, depth) => {
        try {
            const endpoint = depth === 0
                ? `/messageboard/messages/${id}/${type}/`
                : `/messageboard/replies/${id}/${type}/`;

            const response = await axiosInstance.post(endpoint);

            const updateVotes = (items) =>
                items.map((item) => {
                    if (item.id === id) {
                        return { ...item, upvotes: response.data.upvotes, downvotes: response.data.downvotes };
                    } else if (item.replies && item.replies.length > 0) {
                        return { ...item, replies: updateVotes(item.replies) };
                    }
                    return item;
                });

            setMessages(updateVotes(messages));
        } catch (error) {
            console.error(`Error ${type}voting at depth ${depth}:`, error);
        }
    };

    // Prepare API endpoint for CreatePost component
    const createPostEndpoint = '/messageboard/messages/';

    return (
        <div className="messageboard-shell">
            <div className="messageboard-content">
                {!postId && (
                    <div className="mb-header-row">
                        <h2 className="mb-header-title">Discussion</h2>
                        <CreatePost
                            apiEndpoint={createPostEndpoint}
                            onPostCreated={handlePostCreated}
                            courseId={course_id}
                        />
                    </div>
                )}

                <ul style={{ padding: 0, margin: 0 }}>
                    {messages.map((message) => (
                        <Comment
                            key={message.id}
                            comment={message}
                            messageId={message.id}
                            onReply={handleReplySubmit}
                            onVote={handleVote}
                            depth={0}
                            isTopLevel={true}
                            currentUser={currentUser}
                            setMessages={setMessages}
                        />
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default MessageBoard;